import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Grid from '@mui/material/Grid2'
import { useTranslation } from 'react-i18next'
import { IAbout, IItemVisibility, ILayout } from 'types/layoutInterfaces'
import { chooseDBTranslation } from 'utils/translations'
import VisibilityCheckbox from '../VisibilityCheckbox'
import colors from 'constants/colors'

interface IAboutProps {
  about: IAbout
  layout: ILayout
  hideSection: (value: boolean, type: string) => void
  expanded: boolean
  setStatus: (value: boolean) => void
}

/**
 * Renders the About section of the page.
 *
 * @param {Object} about - The about object containing information about the section.
 * @param {string} layout - The layout of the page.
 * @param {boolean} hideSection - Whether to hide the section or not.
 * @param {boolean} expanded - Whether the accordion is expanded or not.
 * @param {function} setStatus - Function to set the status of the accordion.
 * @return {React.ReactElement} The rendered About section.
 * @notExported
 */
const About: React.FC<IAboutProps> = ({ about, layout, hideSection, expanded, setStatus }) => {
  const { t, i18n } = useTranslation()
  const description = chooseDBTranslation(i18n, about).about

  let hidden = false
  if (layout && layout.ItemVisibilities && layout.ItemVisibilities.length > 0) {
    const visibilities = layout.ItemVisibilities.filter((visibility: IItemVisibility) => visibility.type === 'about')
    if (visibilities.length > 0) {
      hidden = visibilities.every((value: IItemVisibility) => value.hidden === true)
    }
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={(_e, expanded) => setStatus(expanded)}
      elevation={0}
      sx={{ borderRadius: '5px', border: `1px solid ${colors.borderColor}` }}
    >
      <Box display="flex" alignItems="center">
        <VisibilityCheckbox checked={hidden} onChange={(checked: boolean) => hideSection(checked, 'about')} />
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            flex: 1,
            fontSize: '20px',
            color: theme => theme.palette.primary.main,
            fontWeight: 'bold',
          }}
        >
          <Grid> {t('print.layout.about')}</Grid>
        </AccordionSummary>
      </Box>
      <AccordionDetails>{description}</AccordionDetails>
    </Accordion>
  )
}

export default About
